// The browser's own view of each tRPC call: time to headers and to the end of the body, keyed by the x-span-id the
// call carried, so a `client_fetch` event joins the server's `request` event for the same call.
import type { ClientFetchInput } from 'shared/wide-event-kinds'

type Fetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

let sink: ((fetch: ClientFetchInput) => void) | undefined

/** session-actions registers it only with the wide-events sink on; until then the wrapper is a pass-through. */
export function registerFetchSink(next: (fetch: ClientFetchInput) => void): void {
  sink = next
}

/** `/trpc/jobs.list,auth.getUser?batch=1` → `jobs.list,auth.getUser`. */
export function trpcMethods(url: string): string {
  const path = url.split('?')[0] ?? ''
  const at = path.lastIndexOf('/trpc/')
  if (at < 0) return ''
  try {
    return decodeURIComponent(path.slice(at + '/trpc/'.length))
  } catch {
    return path.slice(at + '/trpc/'.length)
  }
}

const urlOf = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

/** The same body, calling `onEnd` once it is read to the end, fails, or is cancelled by its reader. */
export function onBodyEnd(
  body: ReadableStream<Uint8Array>,
  onEnd: (how: 'done' | 'error' | 'cancel') => void,
): ReadableStream<Uint8Array> {
  const reader = body.getReader()
  let ended = false
  const end = (how: 'done' | 'error' | 'cancel'): void => {
    if (ended) return
    ended = true
    onEnd(how)
  }
  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const chunk = await reader.read()
        if (chunk.done) {
          controller.close()
          end('done')
        } else controller.enqueue(chunk.value)
      } catch (err) {
        controller.error(err)
        end('error')
      }
    },
    cancel(reason) {
      end('cancel')
      return reader.cancel(reason)
    },
  })
}

/** Wrap a fetch so each call reports a `ClientFetchInput`. Looks the transport up per call, never captures it. */
export function timedFetch(transport: Fetch = (input, init) => fetch(input, init)): Fetch {
  return async (input, init) => {
    const report = sink
    if (!report) return transport(input, init)
    const startedAt = performance.now()
    const t = Date.now()
    const headers = new Headers(init?.headers)
    const base = {
      t,
      trace: headers.get('x-trace-id') ?? undefined,
      span: headers.get('x-span-id') ?? undefined,
      methods: trpcMethods(urlOf(input)),
    }
    let response: Response
    try {
      response = await transport(input, init)
    } catch (err) {
      const elapsed = performance.now() - startedAt
      const aborted = err instanceof DOMException && err.name === 'AbortError'
      report({ ...base, status: 0, ttfbMs: elapsed, ms: elapsed, aborted })
      throw err
    }
    const ttfbMs = performance.now() - startedAt
    // The batch-stream link cancels the body after its last result, and an aborted call's read fails: either is an
    // abort only with the signal. A read that fails without it is a connection lost mid-stream.
    const finish = (how: 'done' | 'error' | 'cancel'): void => {
      const aborted = how !== 'done' && init?.signal?.aborted === true
      const streamError = how === 'error' && !aborted
      report({
        ...base,
        status: response.status,
        ttfbMs,
        ms: performance.now() - startedAt,
        aborted,
        ...(streamError ? { streamError } : {}),
      })
    }
    if (!response.body) {
      finish('done')
      return response
    }
    return new Response(onBodyEnd(response.body, finish), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    })
  }
}
